/**
 * Compute a structured diff between two Season values.
 *
 * The diff is grouped by date (the canonical key for the admin editor) so
 * each entry maps cleanly to a row in the diff viewer. Sessions are matched
 * by index within the day; exercises are matched by name first, then by
 * (sets/reps) signature so a renamed exercise on the same slot still
 * registers as `modified` instead of `added` + `removed`.
 *
 * No mutations: callers can safely pass the live Zustand store value.
 */
import type { Exercise, Season, Session, WorkoutDay, DayData } from '../types/workout';
import { isRestDay, getDaySessions } from '../types/workout';

export type ExerciseFieldChange = {
  field: 'exercise' | 'sets' | 'reps' | 'weight';
  before: string | number;
  after: string | number;
};

export type ExerciseDiff =
  | { kind: 'added'; sessionIndex: number; exerciseIndex: number; exercise: Exercise }
  | { kind: 'removed'; sessionIndex: number; exerciseIndex: number; exercise: Exercise }
  | {
      kind: 'modified';
      sessionIndex: number;
      exerciseIndex: number;
      before: Exercise;
      after: Exercise;
      changes: ExerciseFieldChange[];
    };

export type SessionDiff =
  | { kind: 'session_added'; sessionIndex: number; session: Session }
  | { kind: 'session_removed'; sessionIndex: number; session: Session }
  | { kind: 'session_renamed'; sessionIndex: number; before: string; after: string };

export type DayDiff = {
  date: string;
  before: DayData | null;
  after: DayData | null;
  status:
    | 'unchanged'
    | 'added'
    | 'removed'
    | 'rest_to_workout'
    | 'workout_to_rest'
    | 'goal_changed'
    | 'category_changed'
    | 'sessions_changed'
    | 'exercises_changed';
  goalBefore?: string;
  goalAfter?: string;
  categoryBefore?: string;
  categoryAfter?: string;
  sessionDiffs: SessionDiff[];
  exerciseDiffs: ExerciseDiff[];
};

export type SeasonDiff = {
  /** Days that have any change. Sorted ascending by date. */
  changedDays: DayDiff[];
  /** Stable summary (counts) for the header. */
  summary: {
    daysAdded: number;
    daysRemoved: number;
    daysModified: number;
    exercisesAdded: number;
    exercisesRemoved: number;
    exercisesModified: number;
    sessionsAdded: number;
    sessionsRemoved: number;
    weeksAdded: number;
    weeksRemoved: number;
  };
};

function flattenDays(season: Season | null | undefined): Map<string, DayData> {
  const out = new Map<string, DayData>();
  if (!season) return out;
  for (const cycle of season.cycles ?? []) {
    for (const week of cycle.weeks ?? []) {
      for (const [date, day] of Object.entries(week.days ?? {})) {
        out.set(date, day);
      }
    }
  }
  return out;
}

function countWeeks(season: Season | null | undefined): number {
  if (!season) return 0;
  return (season.cycles ?? []).reduce((sum, c) => sum + (c.weeks?.length ?? 0), 0);
}

function exerciseSignature(ex: Exercise | undefined | null): string {
  if (!ex) return '';
  return `${(ex.exercise || '').toLowerCase().trim()}|${ex.sets ?? ''}|${ex.reps ?? ''}|${ex.weight ?? ''}`;
}

function exerciseChanges(before: Exercise, after: Exercise): ExerciseFieldChange[] {
  const out: ExerciseFieldChange[] = [];
  (['exercise', 'sets', 'reps', 'weight'] as const).forEach((field) => {
    if ((before as any)[field] !== (after as any)[field]) {
      out.push({ field, before: (before as any)[field] ?? '', after: (after as any)[field] ?? '' });
    }
  });
  return out;
}

/**
 * Match exercises across two lists.
 *
 * - Same-name pairs first (most stable).
 * - Then by exact signature (covers renames where everything else matches).
 * - Then positional fallback for whatever's left.
 */
function diffExerciseLists(
  beforeList: Exercise[],
  afterList: Exercise[],
  sessionIndex: number,
): ExerciseDiff[] {
  const out: ExerciseDiff[] = [];
  const remainingAfter = afterList.map((ex, i) => ({ ex, i, taken: false }));
  const usedBeforeIdx = new Set<number>();

  // Pass 1: name match (case-insensitive). Prefer same-position pairs first so
  // duplicate exercise names within a single session don't cross-match.
  beforeList.forEach((b, i) => {
    const key = (b.exercise || '').toLowerCase().trim();
    if (!key) return;
    let m = remainingAfter.find(
      (r) => !r.taken && r.i === i && (r.ex.exercise || '').toLowerCase().trim() === key,
    );
    if (!m) {
      m = remainingAfter.find(
        (r) => !r.taken && (r.ex.exercise || '').toLowerCase().trim() === key,
      );
    }
    if (m) {
      m.taken = true;
      usedBeforeIdx.add(i);
      const changes = exerciseChanges(b, m.ex);
      if (changes.length > 0) {
        out.push({ kind: 'modified', sessionIndex, exerciseIndex: m.i, before: b, after: m.ex, changes });
      }
    }
  });

  // Pass 2: signature match (no-op rename detector)
  beforeList.forEach((b, i) => {
    if (usedBeforeIdx.has(i)) return;
    const sig = exerciseSignature(b);
    const m = remainingAfter.find((r) => !r.taken && exerciseSignature(r.ex) === sig);
    if (m) {
      m.taken = true;
      usedBeforeIdx.add(i);
      // Same signature -> identical content; no change to record.
    }
  });

  // Pass 3: positional fallback (renames + edits at the same slot)
  beforeList.forEach((b, i) => {
    if (usedBeforeIdx.has(i)) return;
    const m = remainingAfter.find((r) => !r.taken && r.i === i);
    if (m) {
      m.taken = true;
      usedBeforeIdx.add(i);
      const changes = exerciseChanges(b, m.ex);
      if (changes.length > 0) {
        out.push({ kind: 'modified', sessionIndex, exerciseIndex: m.i, before: b, after: m.ex, changes });
      }
    }
  });

  beforeList.forEach((b, i) => {
    if (!usedBeforeIdx.has(i)) {
      out.push({ kind: 'removed', sessionIndex, exerciseIndex: i, exercise: b });
    }
  });

  remainingAfter.forEach((r) => {
    if (!r.taken) {
      out.push({ kind: 'added', sessionIndex, exerciseIndex: r.i, exercise: r.ex });
    }
  });

  return out;
}

function diffDay(date: string, before: DayData | null, after: DayData | null): DayDiff {
  if (before === null && after === null) {
    return {
      date,
      before,
      after,
      status: 'unchanged',
      sessionDiffs: [],
      exerciseDiffs: [],
    };
  }
  if (before === null) {
    return {
      date,
      before,
      after,
      status: 'added',
      sessionDiffs: [],
      exerciseDiffs: [],
    };
  }
  if (after === null) {
    return {
      date,
      before,
      after,
      status: 'removed',
      sessionDiffs: [],
      exerciseDiffs: [],
    };
  }

  const beforeRest = isRestDay(before);
  const afterRest = isRestDay(after);
  if (beforeRest && !afterRest) {
    return {
      date,
      before,
      after,
      status: 'rest_to_workout',
      sessionDiffs: [],
      exerciseDiffs: [],
    };
  }
  if (!beforeRest && afterRest) {
    return {
      date,
      before,
      after,
      status: 'workout_to_rest',
      sessionDiffs: [],
      exerciseDiffs: [],
    };
  }
  if (beforeRest && afterRest) {
    // Rest days can still differ by category / day_name / inner sessions.
    const sameMeta = JSON.stringify(before) === JSON.stringify(after);
    return {
      date,
      before,
      after,
      status: sameMeta ? 'unchanged' : 'sessions_changed',
      sessionDiffs: [],
      exerciseDiffs: [],
    };
  }

  const beforeWD = before as WorkoutDay;
  const afterWD = after as WorkoutDay;
  const beforeSessions = getDaySessions(beforeWD);
  const afterSessions = getDaySessions(afterWD);
  const maxLen = Math.max(beforeSessions.length, afterSessions.length);
  const sessionDiffs: SessionDiff[] = [];
  const exerciseDiffs: ExerciseDiff[] = [];

  for (let i = 0; i < maxLen; i++) {
    const bs = beforeSessions[i];
    const as = afterSessions[i];
    if (!bs && as) {
      sessionDiffs.push({ kind: 'session_added', sessionIndex: i, session: as });
      (as.exercises ?? []).forEach((ex, ei) => {
        exerciseDiffs.push({ kind: 'added', sessionIndex: i, exerciseIndex: ei, exercise: ex });
      });
      continue;
    }
    if (bs && !as) {
      sessionDiffs.push({ kind: 'session_removed', sessionIndex: i, session: bs });
      (bs.exercises ?? []).forEach((ex, ei) => {
        exerciseDiffs.push({ kind: 'removed', sessionIndex: i, exerciseIndex: ei, exercise: ex });
      });
      continue;
    }
    if (bs && as) {
      const bn = (bs.name || bs.category || '').trim();
      const an = (as.name || as.category || '').trim();
      if (bn !== an) {
        sessionDiffs.push({ kind: 'session_renamed', sessionIndex: i, before: bn, after: an });
      }
      // Surface session-level metadata edits (goal, priorities, reason) as a
      // rename row so they show up in the diff. This is a pragmatic way to
      // represent "session content changed" without a separate diff kind.
      const metaChanged =
        (bs.goal ?? '') !== (as.goal ?? '') ||
        (bs.reason ?? '') !== (as.reason ?? '') ||
        JSON.stringify(bs.priorities ?? []) !== JSON.stringify(as.priorities ?? []);
      if (metaChanged && bn === an) {
        sessionDiffs.push({
          kind: 'session_renamed',
          sessionIndex: i,
          before: bn || `Session ${i + 1}`,
          after: `${an || `Session ${i + 1}`} (metadata changed)`,
        });
      }
      const innerDiffs = diffExerciseLists(bs.exercises ?? [], as.exercises ?? [], i);
      exerciseDiffs.push(...innerDiffs);
    }
  }

  const goalBefore = beforeWD.goal ?? '';
  const goalAfter = afterWD.goal ?? '';
  const categoryBefore = beforeWD.category ?? '';
  const categoryAfter = afterWD.category ?? '';

  let status: DayDiff['status'] = 'unchanged';
  if (categoryBefore !== categoryAfter) status = 'category_changed';
  if (goalBefore !== goalAfter) status = status === 'category_changed' ? 'sessions_changed' : 'goal_changed';
  if (sessionDiffs.length > 0) status = 'sessions_changed';
  if (exerciseDiffs.length > 0) status = status === 'unchanged' ? 'exercises_changed' : 'sessions_changed';

  return {
    date,
    before,
    after,
    status,
    goalBefore: goalBefore || undefined,
    goalAfter: goalAfter || undefined,
    categoryBefore: categoryBefore || undefined,
    categoryAfter: categoryAfter || undefined,
    sessionDiffs,
    exerciseDiffs,
  };
}

export function diffSeasons(before: Season | null | undefined, after: Season | null | undefined): SeasonDiff {
  const b = flattenDays(before);
  const a = flattenDays(after);
  const allDates = new Set<string>([...b.keys(), ...a.keys()]);
  const sortedDates = Array.from(allDates).sort();

  const changedDays: DayDiff[] = [];
  const summary = {
    daysAdded: 0,
    daysRemoved: 0,
    daysModified: 0,
    exercisesAdded: 0,
    exercisesRemoved: 0,
    exercisesModified: 0,
    sessionsAdded: 0,
    sessionsRemoved: 0,
    weeksAdded: Math.max(0, countWeeks(after) - countWeeks(before)),
    weeksRemoved: Math.max(0, countWeeks(before) - countWeeks(after)),
  };

  for (const date of sortedDates) {
    const bd = b.get(date) ?? null;
    const ad = a.get(date) ?? null;
    const dd = diffDay(date, bd, ad);
    if (dd.status === 'unchanged') continue;
    changedDays.push(dd);
    if (dd.status === 'added') summary.daysAdded++;
    else if (dd.status === 'removed') summary.daysRemoved++;
    else summary.daysModified++;

    for (const ed of dd.exerciseDiffs) {
      if (ed.kind === 'added') summary.exercisesAdded++;
      else if (ed.kind === 'removed') summary.exercisesRemoved++;
      else if (ed.kind === 'modified') summary.exercisesModified++;
    }
    for (const sd of dd.sessionDiffs) {
      if (sd.kind === 'session_added') summary.sessionsAdded++;
      else if (sd.kind === 'session_removed') summary.sessionsRemoved++;
    }
  }

  return { changedDays, summary };
}

/** True when there are no detectable changes. */
export function diffIsEmpty(diff: SeasonDiff): boolean {
  return diff.changedDays.length === 0 && diff.summary.weeksAdded === 0 && diff.summary.weeksRemoved === 0;
}
